"use client";

import React from "react";
import { ArrowRight, CircleCheck as CheckCircle2, Sparkles } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
interface FloorTypology {
  id: string;
  title: string;
  category: string;
  isFlagship: boolean;
  thickness: string;
  description: string;
  features: string;
  imageSrc: string;
  imageSrcset: string;
  imageSizes: string;
  imageAlt: string;
}
const typologies: FloorTypology[] = [{
  id: "mikrocement",
  title: "Mikrocement",
  category: "Realizacja w całej Polsce",
  isFlagship: true,
  thickness: "2–3 mm",
  description: "Ciągła, wodoodporna powierzchnia bez fug i dylatacji. Doskonale sprawdza się na ogrzewaniu podłogowym, w łazienkach oraz otwartych strefach dziennych.",
  features: "Wysoka odporność na ścieranie, łatwe utrzymanie czystości i aksamitna faktura.",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723616/img/flooring_microcement_living_room.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723616/img/flooring_microcement_living_room.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723616/img/flooring_microcement_living_room.png-wvc-sizes",
  imageAlt: "Gładka posadzka mikrocementowa w nowoczesnym salonie"
}, {
  id: "zywica-epoksydowa",
  title: "Żywica i epoksyd",
  category: "Realizacja w całej Polsce",
  isFlagship: true,
  thickness: "1.5–4 mm",
  description: "Monolityczne systemy żywiczne poliuretanowe i epoksydowe. Przeznaczone do intensywnie eksploatowanych garaży, pracowni, kotłowni oraz surowych wnętrz loftowych.",
  features: "Pełna odporność chemiczna, wytrzymałość na obciążenia mechaniczne i szczelność.",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723616/img/flooring_resin_epoxy_garage.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723616/img/flooring_resin_epoxy_garage.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723616/img/flooring_resin_epoxy_garage.png-wvc-sizes",
  imageAlt: "Gładka posadzka z żywicy epoksydowej w garażu rezydencji"
}, {
  id: "posadzki-dekoracyjne",
  title: "Posadzki dekoracyjne",
  category: "Systemy mineralne i lastryko",
  isFlagship: false,
  thickness: "8–15 mm",
  description: "Mineralne wylewki polerowane, mikroterrazzo i dekoracyjne jastrychy cementowe o naturalnym uziarnieniu, nadające wnętrzom architektoniczny rytm.",
  features: "Wytrzymałość kamienia naturalnego połączona z precyzyjnym matowym wykończeniem.",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723616/img/flooring_decorative_terrazzo_hall.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723616/img/flooring_decorative_terrazzo_hall.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723616/img/flooring_decorative_terrazzo_hall.png-wvc-sizes",
  imageAlt: "Polerowana posadzka dekoracyjna w nowoczesnym holu"
}];
export default function Flooring() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 16
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45,
        ease: "easeOut"
      }
    }
  };
  return <section id="parkiety-i-posadzki" data-nav="light" className="bg-background text-foreground py-20 md:py-28 border-b border-border/70">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
        {/* Section Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 pb-12 md:pb-16 border-b border-border">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 rounded-full border border-border bg-secondary px-3.5 py-1.5 mb-4">
              <span className="size-2 rounded-full bg-accent" />
              <span className="text-xs font-semibold uppercase tracking-wider text-secondary-foreground font-sans">
                Zasięg ogólnopolski
              </span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground font-sans">
              Parkiety i posadzki
            </h2>
            <p className="mt-4 text-base sm:text-lg text-muted-foreground font-sans font-light leading-relaxed">
              Projektujemy i wykonujemy bezspoinowe posadzki z mikrocementu, żywic epoksydowych oraz mineralnych wylewek w nowoczesnych domach, apartamentach i przestrzeniach komercyjnych.
            </p>
          </div>

          <div className="flex items-center gap-3">
            <div className="hidden sm:flex items-center gap-2 text-xs font-mono text-muted-foreground bg-secondary px-3 py-2 rounded-md border border-border">
              <Sparkles className="size-3.5 text-accent" />
              <span>Standard rzemieślniczy</span>
            </div>
          </div>
        </div>

        {/* Asymmetric Cards Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-6 pt-12 md:pt-16">
          {typologies.map((item, i) => {
          // Asymmetric layout span: 5-7 split or 4-4-4
          const colSpan = i === 0 ? "lg:col-span-5" : i === 1 ? "lg:col-span-7" : "lg:col-span-12 md:col-span-2";
          return <motion.div key={item.id} variants={itemVariants} data-index={i} className={colSpan}>
                <Card className="group relative h-full flex flex-col justify-between overflow-hidden border border-border bg-card/85 backdrop-blur-md rounded-xl shadow-sm transition-[transform,border-color,box-shadow] duration-220 hover:-translate-y-1 hover:border-foreground/40 hover:shadow-md">
                  {/* Architectural Datum Accent Notch for Flagship Services */}
                  {item.isFlagship && <div aria-hidden="true" className="absolute top-0 left-8 z-20 h-1 w-12 bg-accent rounded-b-sm" />}

                  <div>
                    {/* Visual Photo Container */}
                    <div className="relative aspect-16/10 sm:aspect-16/9 overflow-hidden bg-muted">
                      <img src={item.imageSrc} data-wvc-srcset={item.imageSrcset} data-wvc-sizes={item.imageSizes} alt={item.imageAlt} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-103" />
                      <div className="absolute inset-0 bg-gradient-to-t from-foreground/60 via-foreground/10 to-transparent pointer-events-none" />

                      <div className="absolute bottom-4 left-6 right-6 flex items-center justify-between z-10">
                        <span className="text-xs font-mono font-medium tracking-wide text-primary-foreground bg-foreground/60 backdrop-blur-md px-2.5 py-1 rounded-md border border-primary-foreground/20">
                          {item.thickness}
                        </span>
                        {item.isFlagship && <span className="text-xs font-sans font-semibold text-accent-foreground bg-accent px-2.5 py-1 rounded-md">
                            Flagowa technologia
                          </span>}
                      </div>
                    </div>

                    {/* Card Content Details */}
                    <CardContent className="p-6 md:p-8">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground font-sans">
                          {item.category}
                        </span>
                      </div>

                      <h3 className="text-2xl font-bold tracking-tight text-foreground font-sans">
                        {item.title}
                      </h3>

                      <p className="mt-3 text-sm md:text-base text-muted-foreground font-sans font-light leading-relaxed">
                        {item.description}
                      </p>

                      <div className="mt-5 pt-5 border-t border-border flex items-start gap-2.5 text-xs sm:text-sm text-foreground/80 font-sans">
                        <CheckCircle2 className="size-4 shrink-0 text-accent mt-0.5" />
                        <span>{item.features}</span>
                      </div>
                    </CardContent>
                  </div>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {/* Section Bottom CTA */}
        <div className="mt-14 sm:mt-18 pt-10 border-t border-border flex flex-col sm:flex-row items-center justify-between gap-6">
          <p className="text-sm text-muted-foreground font-sans text-center sm:text-left">
            Szukasz precyzyjnego wykonania posadzki na inwestycji? Sprawdź pełną ofertę i realizacje.
          </p>

          <Button asChild variant="secondary" size="lg" className="group px-7 py-3.5 font-sans font-medium text-secondary-foreground hover:bg-foreground hover:text-background transition-colors">
            <Link to="/ParkietyiposadzkiPage" className="inline-flex items-center gap-2.5">
              <span>Zobacz posadzki i parkiety</span>
              <ArrowRight className="size-4 transition-transform duration-200 group-hover:translate-x-1" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}